import { useEffect, useRef } from 'react'

interface NavState {
  focusedNavIndex: number | null
  selectedIds: Set<string>
  flatNavList: { id: string; type: string }[]
  clearSelection: () => void
  setFocusedNavIndex: (idx: number | null) => void
  setFocusedNavIndexWithAnchor: (idx: number | null) => void
  setSelectedIds: (ids: Set<string>) => void
  wrapperRef: React.RefObject<HTMLDivElement | null>
}

interface UseSelectionCacheParams {
  projectId: string | undefined
  navigation: NavState
  pendingRestoreRef: React.MutableRefObject<string | null>
  pendingSelectionRestoreRef: React.MutableRefObject<string | null>
  correctDataReady: boolean
  selectionCacheRef: React.MutableRefObject<Record<string, { focusedId: string | null; selectedIds: string[] }>>
}

export function useSelectionCache({
  projectId,
  navigation,
  pendingRestoreRef,
  pendingSelectionRestoreRef,
  correctDataReady,
  selectionCacheRef,
}: UseSelectionCacheParams): void {
  // Continuously save selection state for the current project on every render.
  // This ensures the cache is always up-to-date BEFORE a tab switch clears the state.
  // Guard: skip while a restore is pending (projectId just changed, old nav state still active).
  // Only write when values actually changed to avoid unnecessary array allocations.
  // Skip empty writes: various global click-outside / clearSelection handlers
  // fire right before the page unmounts on a tab switch, and saving the
  // transient empty state would wipe the user's real selection.
  const prevSelectionRef = useRef<{ focusedIndex: number | null; selectedIds: Set<string> }>({
    focusedIndex: null, selectedIds: new Set()
  })
  if (projectId && !pendingRestoreRef.current && !pendingSelectionRestoreRef.current && navigation.flatNavList.length > 0) {
    const changed = navigation.focusedNavIndex !== prevSelectionRef.current.focusedIndex
      || navigation.selectedIds !== prevSelectionRef.current.selectedIds
    const isEmpty = navigation.focusedNavIndex === null && navigation.selectedIds.size === 0
    if (changed && !isEmpty) {
      prevSelectionRef.current = {
        focusedIndex: navigation.focusedNavIndex,
        selectedIds: navigation.selectedIds,
      }
      const focusedItem = navigation.focusedNavIndex !== null
        ? navigation.flatNavList[navigation.focusedNavIndex]
        : null
      selectionCacheRef.current[projectId] = {
        focusedId: focusedItem?.id ?? null,
        selectedIds: [...navigation.selectedIds],
      }
    }
  }

  // Restore selection when new data arrives after a tab switch.
  // Must wait for correctDataReady so we don't run against stale (old project) data.
  useEffect(() => {
    if (!pendingSelectionRestoreRef.current || !projectId) return
    if (pendingSelectionRestoreRef.current !== projectId) return
    if (!correctDataReady) return
    if (navigation.flatNavList.length === 0) return

    const cached = selectionCacheRef.current[projectId]
    pendingSelectionRestoreRef.current = null

    // No cached selection → reset so ArrowDown/Up starts fresh (first/last item)
    if (!cached || (!cached.focusedId && cached.selectedIds.length === 0)) {
      navigation.clearSelection()
      return
    }

    // Restore focused item by ID (find its new index in flatNavList).
    // Use the with-anchor setter so shift-select ranges after restore
    // originate from the restored focus, not from a stale/unset anchor.
    if (cached.focusedId) {
      const idx = navigation.flatNavList.findIndex(item => item.id === cached.focusedId)
      if (idx !== -1) {
        navigation.setFocusedNavIndexWithAnchor(idx)
      } else {
        navigation.setFocusedNavIndexWithAnchor(null)
      }
    }
    // Restore selected IDs (filter to only IDs that still exist)
    if (cached.selectedIds.length > 0) {
      const validIds = new Set(navigation.flatNavList.map(item => item.id))
      const restored = cached.selectedIds.filter(id => validIds.has(id))
      if (restored.length > 0) {
        navigation.setSelectedIds(new Set(restored))
      } else {
        navigation.setSelectedIds(new Set())
      }
    }
    // Hand keyboard focus back to the list so Arrow keys work without
    // requiring the user to click on the list first.
    navigation.wrapperRef.current?.focus({ preventScroll: true })
  }, [projectId, correctDataReady, navigation.flatNavList])
}
